import { useEffect, useState } from 'react';
import { Alert, Card, Empty, Input, Select, Spin, Table, Tag, Typography, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';

import LayoutWithMenu from 'components/Layout/LayoutWithMenu';
import { useScreen } from 'hooks/useScreen';

import ParentLink from '../ClubRegistration/ParentLink';
import type { ParentDetails } from '../ClubRegistration/types';
import { queryErrorMessage } from '../ClubRegistration/types';

const { Text } = Typography;

interface ClassOption {
  id: number;
  name: string;
  class_number: number | null;
}

interface StudentRow {
  id: number;
  full_name: string;
  first_name: string; // the server's default order, and the name sort
  code: string;
  status: string | null; // student.status (Төлөв)
  card_balance: number;
  club_count: number; // Memberships (not cancelled)
  lunch_count: number; // paid lunches, all time, less reversed ones
  // Every parent linked to the student.
  parents: ParentDetails[];
}

// student.status options (metadata/student.ts).
const STATUS_COLORS: Record<string, string> = {
  Идэвхтэй: 'green',
  'Шинэ суралцагч': 'blue',
  'Дэвшилтээс элсэх': 'purple',
  'Бүлэг солих': 'orange',
};

function formatMNT(n: number): string {
  return new Intl.NumberFormat('mn-MN').format(n) + '₮';
}

const ManagedClassStudents = () => {
  const { runQuery } = useScreen();
  const [classes, setClasses] = useState<ClassOption[]>([]);
  const [classesLoading, setClassesLoading] = useState(false);
  const [classesError, setClassesError] = useState<string | null>(null);
  const [selectedClassId, setSelectedClassId] = useState<number>();
  const [students, setStudents] = useState<StudentRow[]>([]);
  const [studentsLoading, setStudentsLoading] = useState(false);
  const [search, setSearch] = useState('');

  useEffect(() => {
    setClassesLoading(true);
    runQuery<ClassOption>('class_options')
      .then((items) => {
        setClasses(items);
        if (items.length === 0) {
          setClassesError('Танд харьяалагдах анги алга байна.');
        }
      })
      .catch((e) => setClassesError(queryErrorMessage(e)))
      .finally(() => setClassesLoading(false));
  }, [runQuery]);

  useEffect(() => {
    if (!selectedClassId) {
      setStudents([]);
      return;
    }

    setStudentsLoading(true);
    runQuery<StudentRow>('class_students', { classId: selectedClassId })
      .then(setStudents)
      .catch((e) => message.error(queryErrorMessage(e)))
      .finally(() => setStudentsLoading(false));
  }, [selectedClassId, runQuery]);

  // Column filters/sorters work on the loaded class, in the browser.
  const statusFilters = [...new Set(students.map((s) => s.status).filter((v): v is string => !!v))]
    .sort((a, b) => a.localeCompare(b, 'mn'))
    .map((v) => ({ text: v, value: v }));

  const columns: ColumnsType<StudentRow> = [
    {
      title: 'Код',
      dataIndex: 'code',
      width: 120,
      sorter: (a, b) => (a.code ?? '').localeCompare(b.code ?? ''),
    },
    {
      title: 'Сурагч',
      dataIndex: 'full_name',
      // By first name, like the server's order ("Б. Есүй" sorts under Е).
      sorter: (a, b) => a.first_name.localeCompare(b.first_name, 'mn'),
    },
    {
      title: 'Төлөв',
      dataIndex: 'status',
      filters: statusFilters,
      onFilter: (value, s) => s.status === value,
      sorter: (a, b) => (a.status ?? '').localeCompare(b.status ?? '', 'mn'),
      render: (v: string | null) =>
        v ? <Tag color={STATUS_COLORS[v]}>{v}</Tag> : <Text type="secondary">—</Text>,
    },
    {
      title: 'Асран хамгаалагч',
      dataIndex: 'parents',
      filters: [
        { text: 'Холбогдсон', value: 'linked' },
        { text: 'Холбогдоогүй', value: 'none' },
      ],
      onFilter: (value, s) => (value === 'linked') === s.parents.length > 0,
      sorter: (a, b) => a.parents.length - b.parents.length,
      // One per line; the name opens their details.
      render: (parents: ParentDetails[]) =>
        parents.length ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
            {parents.map((p, i) => (
              <span key={`${p.full_name}-${i}`}>
                <ParentLink name={p.full_name} parent={p} />
                {p.relation && <Text type="secondary"> ({p.relation})</Text>}
              </span>
            ))}
          </div>
        ) : (
          <Text type="secondary">Холбогдоогүй</Text>
        ),
    },
    {
      title: 'Дугуйлан',
      dataIndex: 'club_count',
      align: 'right',
      width: 120,
      filters: [
        { text: 'Гишүүн', value: 'member' },
        { text: 'Гишүүнчлэлгүй', value: 'none' },
      ],
      onFilter: (value, s) => (value === 'member') === s.club_count > 0,
      sorter: (a, b) => a.club_count - b.club_count,
      render: (v: number) => (v ? v : <Text type="secondary">—</Text>),
    },
    {
      title: 'Үдийн хоол',
      dataIndex: 'lunch_count',
      align: 'right',
      width: 120,
      sorter: (a, b) => a.lunch_count - b.lunch_count,
      render: (v: number) => (v ? v : <Text type="secondary">—</Text>),
    },
    {
      title: 'Картын үлдэгдэл',
      dataIndex: 'card_balance',
      align: 'right',
      sorter: (a, b) => a.card_balance - b.card_balance,
      render: formatMNT,
    },
  ];

  const term = search.trim().toLowerCase();
  const visible = term
    ? students.filter(
        (s) =>
          (s.full_name ?? '').toLowerCase().includes(term) ||
          (s.code ?? '').toLowerCase().includes(term),
      )
    : students;

  return (
    <LayoutWithMenu className="px-3 lg:px-5">
      <Card>
        {!classesLoading && classesError && (
          <Alert
            type="warning"
            showIcon
            message={classesError}
            style={{ marginBottom: 24 }}
          />
        )}

        {!classesError && (
          <div style={{ marginBottom: 24 }}>
            <Select
              placeholder="Анги сонгох"
              style={{ width: 320 }}
              loading={classesLoading}
              value={selectedClassId}
              onChange={(value) => setSelectedClassId(value)}
              allowClear
              onClear={() => setSelectedClassId(undefined)}
              options={classes.map((c) => ({
                value: c.id,
                label: c.class_number ? `${c.name} (${c.class_number}-р анги)` : c.name,
              }))}
            />
          </div>
        )}

        {studentsLoading && <Spin />}

        {!studentsLoading && selectedClassId && students.length === 0 && (
          <Empty description="Энэ ангид сурагч алга" />
        )}

        {!studentsLoading && students.length > 0 && (
          <>
            <Input.Search
              placeholder="Нэр, кодоор хайх..."
              allowClear
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ width: 320, marginBottom: 12 }}
            />
            <Table rowKey="id" columns={columns} dataSource={visible} pagination={false} />
          </>
        )}
      </Card>
    </LayoutWithMenu>
  );
};

export default ManagedClassStudents;
